import { GAME_PAGES } from '../../routes/gameScopeNav.jsx';
import { SETTINGS_ACCESS } from '../../pages/game/game-settings/settingsAccess.js';
import { resolvePermissionLevel } from '../../utils/permissionLevel.js';

// The game header's access badge says what the user can do ON THE PAGE THEY ARE ON - the same answer the
// page's own guard and Save buttons give. It used to say "Read / Write" whenever the user held full access to
// anything in the game, so a role with Full on analytics and Read on live ops read "Read / Write" on Configs.
export const READ_WRITE = 'Read / Write';
export const READ_ONLY = 'Read Only';

// Every game page that carries an RBAC page, as { segment, page }, longest segment first so a nested page
// ("live-services/upcoming") wins over a shorter prefix.
const GATED_PAGES = (() => {
    const out = [];
    const walk = (items) => items.forEach((p) => {
        if (p.children) walk(p.children);
        else if (p.page && p.segment) out.push({ segment: p.segment, page: p.page });
    });
    walk(GAME_PAGES);
    return out.sort((a, b) => b.segment.length - a.segment.length);
})();

/** The part of a /game/<slug>/... path after the slug, or null outside a game (or in onboarding). */
const gameSubPath = (pathname) => {
    const m = (pathname || '').match(/^\/game\/([^/]+)(?:\/(.*))?$/);
    if (!m || m[1] === 'new') return null;
    return (m[2] || '').replace(/\/+$/, '');
};

/**
 * The level that decides whether the current page is editable, or null when the page carries no permission
 * of its own (the game overview, Backlog, Calendar, the funnel placeholders) or the path is not a game page.
 * A settings section is editable only with its edit component; one with none (SDK Integration) is read-only.
 */
export const currentPageLevel = (pathname, effectivePermissions) => {
    const sub = gameSubPath(pathname);
    if (sub == null || !effectivePermissions) return null;

    const settings = sub.match(/^settings\/([^/]+)/);
    if (settings) {
        const access = SETTINGS_ACCESS[settings[1]];
        if (!access) return null;
        const pageLevel = resolvePermissionLevel(effectivePermissions, access.page);
        if (pageLevel === 'NO_ACCESS') return 'NO_ACCESS';
        return access.edit ? resolvePermissionLevel(effectivePermissions, access.edit) : 'READ_ONLY';
    }

    const hit = GATED_PAGES.find(p => sub === p.segment || sub.startsWith(`${p.segment}/`));
    return hit ? resolvePermissionLevel(effectivePermissions, hit.page) : null;
};

/** The badge text for the current page: Read / Write, Read Only, or null (no badge). */
export const pageAccessLabel = ({ pathname, effectivePermissions, isSuperUser = false }) => {
    if (isSuperUser) return null; // the super-user badge says it
    const level = currentPageLevel(pathname, effectivePermissions);
    if (level === 'FULL_ACCESS') return READ_WRITE;
    if (level === 'READ_ONLY') return READ_ONLY;
    return null; // NO_ACCESS never renders the page; ungated pages have no level to report
};
